import ExposureIndustryClassLinkerDialog from "./ExposureIndustryClassLinkerDialog";
import {
  Edit as EditIcon,
  Close as CloseIcon,
  Check as SaveIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Grid,
  Stack,
  Alert,
  IconButton,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  ClickAwayListener,
  Box,
  Switch,
} from "@mui/material";
import * as strings from "common/strings";
import Component from "components/Component";
import RawConfigEditor from "components/admin/RawConfigEditor";
import DefaultLoader from "components/common/DefaultLoader";
import EditableTextField from "components/common/EditableTextField";
import InlineSearch from "components/common/InlineSearch";
import ShortcutListener from "components/common/ShortcutListener";
import * as config from "config";
import _ from "lodash";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import * as claimsService from "services/claimsService";
import * as staticDataService from "services/staticDataService";
import { selectImpersonatedCompanyId } from "store/impersonationSlice";
import * as utils from "utils";

const ExposureTableRow = ({
  exposure,
  editing,
  isEditing,
  setEditing,
  updateConfig,
  isUpdating,
  setLinking,
  defaultMeasures,
  retrievedDefaultMeasures,
}) => {
  const saveRequired = isEditing
    ? !_.isEqual(
        {
          name: exposure?.name,
          units: exposure?.units,
          default: defaultMeasures?.[exposure?.key] || false,
        },
        {
          name: editing?.name,
          units: editing?.units,
          default: editing?.default,
        }
      )
    : false;

  const ConditionalClickAwayListener = ({
    listening,
    onClickAway,
    children,
  }) => {
    return listening ? (
      <ClickAwayListener onClickAway={onClickAway}>
        {children}
      </ClickAwayListener>
    ) : (
      <>{children}</>
    );
  };

  return (
    <ConditionalClickAwayListener
      key={exposure?.key}
      listening={isEditing}
      onClickAway={() => {
        if (!saveRequired) {
          setEditing({});
        }
      }}
    >
      <TableRow
        sx={{
          "&:hover .edit-icon": {
            visibility: "visible",
          },
        }}
        hover
        onClick={() => {
          if (!isEditing) {
            setEditing({
              name: exposure?.name,
              key: exposure?.key,
              units: exposure?.units,
              default: defaultMeasures?.[exposure?.key] || false,
            });
          }
        }}
      >
        <TableCell>
          {isEditing ? (
            <EditableTextField
              initialValue={editing?.name || ""}
              align={"left"}
              variant={"filled"}
              size={"small"}
              disabled={isUpdating}
              hiddenLabel
              autoFocus={!saveRequired}
              onSave={(value) => {
                setEditing((edit) => ({
                  ...edit,
                  name: value,
                }));
              }}
            />
          ) : (
            <Typography>{exposure?.name}</Typography>
          )}
        </TableCell>
        <TableCell>
          <Typography variant={"caption"} sx={{ color: "grey" }}>
            {exposure?.key}
          </Typography>
        </TableCell>
        <TableCell>
          {isEditing ? (
            <EditableTextField
              initialValue={editing?.units || ""}
              align={"left"}
              variant={"filled"}
              size={"small"}
              disabled={isUpdating}
              hiddenLabel
              onSave={(value) => {
                setEditing((edit) => ({
                  ...edit,
                  units: value,
                }));
              }}
            />
          ) : (
            <Typography>{exposure?.units}</Typography>
          )}
        </TableCell>
        <TableCell align={"center"} onClick={(e) => e.stopPropagation()}>
          <Tooltip title={"Set as default for all industry classes"}>
            <Switch
              checked={
                isEditing
                  ? editing?.default
                  : defaultMeasures?.[exposure?.key] || false
              }
              onChange={(e) => {
                e.stopPropagation();
                setEditing((prev) => ({
                  ...prev,
                  default: e.target.checked,
                }));
              }}
              disabled={isUpdating || !isEditing}
            />
          </Tooltip>
        </TableCell>
        <TableCell align={"center"}>
          <Tooltip
            title={
              defaultMeasures?.[exposure?.key]
                ? "Cannot link exposures that are set as default for all industry classes"
                : "Link to Industry Classes"
            }
          >
            <span>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setLinking(exposure);
                }}
                disabled={isUpdating || defaultMeasures?.[exposure?.key]}
              >
                <LinkIcon />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
        <TableCell>
          {isEditing ? (
            <Stack direction={"row"} justifyContent={"flex-end"}>
              {isUpdating ? (
                <DefaultLoader size={20} style={{ marginRight: "10px" }} />
              ) : (
                <>
                  <Tooltip title={"Save changes"}>
                    <IconButton
                      onClick={(e) => {
                        if (saveRequired) {
                          const {
                            [editing?.key]: _,
                            ...rest
                          } = defaultMeasures;
                          const updatedDefaultMeasures = [
                            ...(Object.entries(rest || {})?.map(
                              ([key, value]) => key
                            ) || []),
                            ...(editing?.default ? [editing?.key] : []),
                          ];
                          updateConfig(
                            [
                              {
                                name: editing?.name,
                                key: editing?.key,
                                units: editing?.units,
                              },
                            ],
                            {
                              defaultMeasuresByIndustryClass: {
                                ...retrievedDefaultMeasures,
                                ALL: updatedDefaultMeasures,
                              },
                            }
                          );
                          setEditing((edit) => ({
                            ...edit,
                            updating: true,
                          }));
                        } else {
                          setEditing({});
                        }
                        e.stopPropagation();
                      }}
                      color={saveRequired ? "primary" : "secondary"}
                    >
                      <SaveIcon />
                    </IconButton>
                  </Tooltip>
                  <ShortcutListener
                    shortcut={"escape"}
                    singleKey
                    action={() => {
                      setEditing({});
                    }}
                  />
                  <ShortcutListener
                    shortcut={"tab"}
                    singleKey
                    action={() => {
                      setEditing({});
                    }}
                  />
                  <Tooltip title={"Cancel"}>
                    <IconButton
                      onClick={(e) => {
                        setEditing({});
                        e.stopPropagation();
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Stack>
          ) : (
            <IconButton
              className={"edit-icon"}
              sx={{
                visibility: "hidden",
              }}
              disabled={isUpdating}
            >
              <EditIcon />
            </IconButton>
          )}
        </TableCell>
      </TableRow>
    </ConditionalClickAwayListener>
  );
};

const ExposureTable = ({
  exposures,
  defaultMeasures,
  updateConfig,
  isUpdating,
  setLinking,
  retrievedDefaultMeasures,
}) => {
  const [editing, setEditing] = useState({});

  useEffect(() => {
    if (!isUpdating) {
      setEditing({});
    }
  }, [isUpdating]);

  return (
    <TableContainer
      style={{
        height: "70vh",
      }}
    >
      <Table stickyHeader size={"small"}>
        <TableHead>
          <TableRow>
            <TableCell align={"left"} width={"25%"}>
              {"Label"}
            </TableCell>
            <TableCell align={"left"} width={"25%"}>
              {"Key"}
            </TableCell>
            <TableCell align={"left"} width={"10%"}>
              {"Units"}
            </TableCell>
            <TableCell align={"center"} width={"20%"}>
              {"Default for All"}
            </TableCell>
            <TableCell align={"center"} width={"15%"}>
              {"Linked Industry Class"}
            </TableCell>
            <TableCell align={"right"} />
          </TableRow>
        </TableHead>
        <TableBody>
          {(exposures || [])?.map((exposure) => (
            <ExposureTableRow
              key={exposure?.key}
              exposure={exposure}
              editing={editing}
              isEditing={editing?.key === exposure?.key}
              setEditing={setEditing}
              updateConfig={updateConfig}
              isUpdating={editing?.updating && editing?.key === exposure?.key}
              setLinking={setLinking}
              defaultMeasures={defaultMeasures}
              retrievedDefaultMeasures={retrievedDefaultMeasures}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const CreateExposureDialog = ({
  open,
  close,
  exposureKeys,
  updateConfig,
  isUpdating,
  retrievedDefaultMeasures = { ALL: [] },
}) => {
  const [exposures, setExposures] = useState([
    {
      name: "",
      key: "",
      units: "",
      default: false,
    },
  ]);

  const [loading, setLoading] = useState(false);

  const exposureNames = open && exposures?.map((ic) => ic?.name);

  useEffect(() => {
    setExposures((currentExposures) =>
      currentExposures?.map((exposure, index) => {
        const { id, alone } = strings.toUniqueId({
          value: exposure?.name,
          list: [
            ...exposureKeys,
            ...(index > 0
              ? currentExposures?.map((exp) => exp?.key)?.slice(0, index)
              : []),
          ],
          verbose: true,
        });
        return {
          ...exposure,
          key: id,
          unique: alone,
        };
      })
    );
  }, [exposureNames, exposureKeys]);

  useEffect(() => {
    if (!isUpdating && loading) {
      close();
    }
  }, [isUpdating, close, loading]);

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setExposures([
        {
          name: "",
          key: "",
          units: "",
          default: false,
        },
      ]);
    }
  }, [open]);

  const isValid = exposures?.reduce((valid, exposure) => {
    if (exposure?.name?.length < 3 && exposure?.name?.length > 0) {
      return false;
    }
    return valid;
  }, true);

  return (
    <Dialog
      open={open}
      onClose={close}
      PaperProps={{
        sx: {
          top: "20vh",
          width: "800px",
          maxWidth: "90vw",
          margin: "auto",
          position: "absolute",
        },
      }}
    >
      <DialogTitle>
        <Stack
          justifyContent={"space-between"}
          direction={"row"}
          alignItems={"center"}
        >
          <Typography variant={"h6"}>{"New Exposure"}</Typography>
          <IconButton onClick={close}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <TableContainer
            style={{
              maxHeight: 600,
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align={"left"} width={"25%"}>
                    {"Label"}
                  </TableCell>
                  <TableCell align={"left"} width={"25%"}>
                    {"Key"}
                  </TableCell>
                  <TableCell align={"left"} width={"15%"}>
                    {"Units"}
                  </TableCell>
                  <TableCell align={"center"} width={"10%"}>
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      spacing={1}
                    >
                      <Tooltip
                        title={
                          "When enabled, this exposure will be selected by default in the industry class dialog"
                        }
                      >
                        <Typography>{"Default for All"}</Typography>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell align={"center"} width={"1%"} />
                </TableRow>
              </TableHead>
              <TableBody sx={{ background: "white" }}>
                {exposures?.map((exposure, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <TextField
                          hiddenLabel
                          autoFocus
                          value={exposure?.name}
                          align={"left"}
                          variant={"filled"}
                          size={"small"}
                          sx={{ width: "100%" }}
                          helperText={
                            (!exposure?.unique ||
                              (exposure?.name?.length < 3 &&
                                exposure?.name?.length > 0)) && (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  top: "8px",
                                  position: "relative",
                                }}
                              >
                                <InfoIcon
                                  fontSize={"small"}
                                  color={!exposure?.unique ? "info" : "error"}
                                />
                                <span>
                                  {!exposure?.unique
                                    ? "A similar exposure exists already."
                                    : "Exposure label too short"}
                                </span>
                              </span>
                            )
                          }
                          onChange={(e) => {
                            setExposures(
                              exposures?.map((exp, i) => {
                                if (i === index) {
                                  return {
                                    ...exp,
                                    name: e.target.value,
                                  };
                                }
                                return exp;
                              })
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <TextField
                          hiddenLabel
                          value={exposure?.key}
                          align={"left"}
                          variant={"filled"}
                          size={"small"}
                          sx={{ width: "100%" }}
                          disabled
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }} align={"center"}>
                        <TextField
                          hiddenLabel
                          autoFocus
                          value={exposure?.units}
                          align={"left"}
                          variant={"filled"}
                          size={"small"}
                          sx={{ width: "100%" }}
                          onChange={(e) => {
                            setExposures(
                              exposures?.map((exp, i) => {
                                if (i === index) {
                                  return {
                                    ...exp,
                                    units: e.target.value,
                                  };
                                }
                                return exp;
                              })
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }} align={"center"}>
                        <Switch
                          checked={exposure?.default || false}
                          onChange={(e) => {
                            setExposures(
                              exposures?.map((exp, i) => ({
                                ...exp,
                                default:
                                  i === index
                                    ? e.target.checked
                                    : exp?.default || false,
                              }))
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        {exposures?.length > 1 ? (
                          <IconButton
                            onClick={() => {
                              setExposures((currentExposures) =>
                                currentExposures?.filter(
                                  (exp, i) => i !== index
                                )
                              );
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        ) : (
                          <div style={{ width: "40px" }}></div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Button
              sx={{
                color: "lightslategray",
              }}
              variant={"outlined"}
              disableElevation
              color={"secondary"}
              onClick={() => {
                setExposures((currentExposures) => [
                  ...currentExposures,
                  {
                    name: "",
                    key: "",
                    units: "",
                    default: false,
                  },
                ]);
              }}
            >
              {"Add row"}
            </Button>
            <Button
              variant={"contained"}
              disableElevation
              color={"primary"}
              disabled={!isValid || loading}
              sx={{ height: "40px" }}
              onClick={() => {
                updateConfig(
                  exposures
                    ?.filter((exp) => exp?.key?.length > 3)
                    ?.map((exp) => ({
                      key: exp?.key,
                      name: exp?.name,
                      units: exp?.units,
                    })),
                  {
                    defaultMeasuresByIndustryClass: {
                      ...retrievedDefaultMeasures,
                      ALL: [
                        ...(retrievedDefaultMeasures?.ALL || []),
                        ...(exposures
                          ?.filter((exp) => exp?.default)
                          ?.map((exp) => exp?.key) || []),
                      ],
                    },
                  }
                );
                setLoading(true);
              }}
            >
              {loading ? <DefaultLoader size={20} /> : "Create"}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const ExposureConfig = () => {
  const [exposures, setExposures] = useState(null);
  const [defaultMeasures, setDefaultMeasures] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [saved, setSaved] = useState(false);
  const [creating, setCreating] = useState(false);
  const [linking, setLinking] = useState(null);
  const impersonatedCompanyId = useSelector(selectImpersonatedCompanyId);

  const {
    data: retrievedExposures,
    isLoading: exposuresLoading,
    refetch: refetchExposures,
  } = claimsService.useExposuresQuery();

  const {
    data: retrievedDefaultMeasures,
    isLoading: defaultMeasuresLoading,
    refetch: refetchDefaultMeasures,
  } = claimsService.useDefaultMeasuresByIndustryClassQuery();

  const [
    triggerUpdateConfig,
    { isLoading: isUpdating, isError },
  ] = staticDataService.useAdminUpdateConfigMutation();

  useEffect(() => {
    if (retrievedExposures != null) {
      setExposures(retrievedExposures);
    }
  }, [retrievedExposures]);

  useEffect(() => {
    if (retrievedDefaultMeasures != null) {
      setDefaultMeasures(
        Object.fromEntries(
          (retrievedDefaultMeasures?.ALL || [])?.map((key) => [key, true])
        )
      );
    }
  }, [retrievedDefaultMeasures]);

  useEffect(() => {
    refetchExposures();
    refetchDefaultMeasures();
  }, [refetchDefaultMeasures, refetchExposures]);

  const updateConfig = (values, additionalConfig = {}) => {
    const updatedExposures = [
      ...exposures?.map((exposure) => {
        const updatedExposure = values.find(
          (value) => value?.key === exposure?.key
        );
        if (updatedExposure) {
          values.splice(values.indexOf(updatedExposure), 1);
          return updatedExposure;
        }
        return exposure;
      }),
      ...values,
    ];
    triggerUpdateConfig({
      key: "exposures",
      value: {
        measures: updatedExposures.map((exp) => ({
          key: exp?.key,
          measure: exp?.name,
          unit: exp?.units,
        })),
        ...additionalConfig,
      },
    }).then((resp) => {
      setDefaultMeasures(
        Object.fromEntries(
          (
            resp?.data?.defaultMeasuresByIndustryClass?.ALL || []
          )?.map((key) => [key, true])
        )
      );
      setExposures(
        resp?.data?.measures?.map((exp) => ({
          key: exp?.key,
          name: exp?.measure,
          units: exp?.unit,
        }))
      );
      clearTimeout(saved);
      setSaved(
        setTimeout(() => {
          setSaved(false);
        }, 5000)
      );
    });
  };

  const rawConfigData = useMemo(() => {
    if (retrievedExposures == null || retrievedDefaultMeasures == null)
      return null;
    return {
      measures: retrievedExposures,
      defaultMeasuresByIndustryClass: retrievedDefaultMeasures,
    };
  }, [retrievedExposures, retrievedDefaultMeasures]);

  const pollForChanges = useCallback(async () => {
    const [measuresResp, defaultsResp] = await Promise.all([
      utils.authenticatedFetch(
        config.endpoints.claims("exposure/types"),
        { method: "get" },
        impersonatedCompanyId
      ),
      utils.authenticatedFetch(
        config.endpoints.claims("exposure/default-measures-by-industry-class"),
        { method: "get" },
        impersonatedCompanyId
      ),
    ]);
    const measures = await measuresResp.json();
    const defaultMeasuresByIndustryClass = await defaultsResp.json();
    return { measures, defaultMeasuresByIndustryClass };
  }, [impersonatedCompanyId]);

  const searchedExposures = (exposures || [])?.filter((exposure) =>
    exposure?.name?.toLowerCase()?.includes(searchText)
  );

  return (
    <Component title={"Edit Exposures"}>
      <RawConfigEditor
        superUserOnly
        data={rawConfigData}
        onSave={async (parsedJson) => {
          await triggerUpdateConfig({
            key: "exposures",
            value: parsedJson,
          });
          refetchExposures();
          refetchDefaultMeasures();
        }}
        isSaving={isUpdating}
        pollForChanges={pollForChanges}
        header={
          <InlineSearch
            placeholder={"Quick Search"}
            onChange={(text) => setSearchText(text.toLowerCase())}
          />
        }
        actions={
          <Button
            variant={"contained"}
            disableElevation
            color={"primary"}
            onClick={() => setCreating(true)}
            startIcon={<AddIcon />}
          >
            {"Add"}
          </Button>
        }
      >
        <Stack spacing={2}>
          {searchedExposures?.length > 0 &&
          !defaultMeasuresLoading &&
          !exposuresLoading &&
          defaultMeasures != null ? (
            <ExposureTable
              exposures={searchedExposures}
              updateConfig={updateConfig}
              isUpdating={isUpdating}
              setLinking={setLinking}
              defaultMeasures={defaultMeasures}
              retrievedDefaultMeasures={retrievedDefaultMeasures}
            />
          ) : (
            <Box
              sx={{
                height: "70vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {defaultMeasuresLoading || exposuresLoading || !exposures ? (
                <DefaultLoader size={64} />
              ) : (
                <Typography>{`No matches found for '${searchText}'`}</Typography>
              )}
            </Box>
          )}
        </Stack>
      </RawConfigEditor>
      <CreateExposureDialog
        open={creating}
        close={() => setCreating(false)}
        exposureKeys={exposures?.map((exp) => exp?.key) || []}
        updateConfig={updateConfig}
        isUpdating={isUpdating}
        retrievedDefaultMeasures={retrievedDefaultMeasures}
      />
      <ExposureIndustryClassLinkerDialog
        open={!!linking}
        close={() => setLinking(null)}
        selectedExposure={linking}
      />
      <Grid xs={12} item>
        {isError ? (
          <Alert severity={"error"}>
            {
              "An error has occured when saving, please contact support at: support@cactus.bm"
            }
          </Alert>
        ) : saved !== false ? (
          <Alert severity={"success"}>
            {
              "Exposures updated, we will be in touch should further details be required."
            }
          </Alert>
        ) : (
          <></>
        )}
      </Grid>
    </Component>
  );
};

export default ExposureConfig;
